import axios, { AxiosResponse } from 'axios';
import { generatePath } from 'react-router-dom';
import {
  PROBLEM_SETS_API_URL,
  PROBLEM_API_URL,
  PROBLEM_SUBMISSION_API_URL,
  PROBLEM_STATS_API_URL,
  PROBLEM_LIKE_API_URL,
  PROBLEM_DISLIKE_API_URL
} from '../config/constants';

// child problem response body
export interface ChildProblemResponse {
  id: number
  allow_testcases: boolean
  create_table_query?: string
  insert_table_query?: string
};

// parent problem response body
export interface ProblemResponse {
  id: number
  child_problem?: ChildProblemResponse
  date_added: string
  previous: number | null
  title: string
  slug: string | null
  type: string
  difficulty: string
  solved_count: number
  is_solved: boolean
  likes: number
  dislikes: number
  liked: boolean
  disliked: boolean
  contributors: string[]
};

// problem set response body
export interface ProblemSetResponse {
  id: number
  title: string
  icon: string | null
};

// parent nested problem response body
export interface ProblemNestedResponse extends ProblemResponse {
  problemset: ProblemSetResponse
  description: string
  starter_code: string
};

// nested problem set response body
export interface ProblemSetNestedResponse extends ProblemSetResponse {
  problems: ProblemResponse[]
};

// get list of active problem sets
export const getProblemSets = async (accessToken: string | null): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.get(
    PROBLEM_SETS_API_URL,
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        ...(accessToken && {
          Authorization: `JWT ${accessToken}`
        })
      }
    }
  );

  return response;
};

// get problem by ID
export const getProblem = async (accessToken: string | null, problemIDOrSlug: number | string): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.get(
    generatePath(PROBLEM_API_URL, { key: problemIDOrSlug.toString() }),
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        ...(accessToken && {
          Authorization: `JWT ${accessToken}`
        })
      }
    },
  );

  return response;
};

// problem submission response body
export interface SubmitProblemResponse {
  run_info: {
    code: number
    output: {
      computed: string
      expected: string
      failing_testcase: string
      outcome: boolean
      efficiency_score: number
      passed_testcases: number
      total_testcases: number
      stderr: string
      stdout: string
      raw?: string
    }
    signal: string | null
    stderr: string
    stdout: string
  }
};

// submit solution to problem
export const submitProblem = async (
  accessToken: string | null,
  problemIDOrSlug: number | string,
  submissionCode: string,
  testcase?: any[]
): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.post(
    generatePath(PROBLEM_SUBMISSION_API_URL, { key: problemIDOrSlug.toString() }),
    {
      submission_code: submissionCode,
      ...testcase !== undefined && { testcase }
    },
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && {
          Authorization: `JWT ${accessToken}`
        })
      }
    }
  );

  return response;
};

export interface ProblemBadge {
  title: string
  url: string
  redirect: string | null
  size: number
};

export interface ProblemStatsTopUser {
  username: string | null
  github_username: string | null
  display_username: string
  solved_problems_count: number
  badges: ProblemBadge[]
};

export interface ProblemStatsResponse {
  solved_problems_count: number
  total_problems_count: number
  completed_problemsets_count: number
  total_problemsets_count: number
  solved_problems_beginner_count: number
  total_problems_beginner_count: number
  solved_problems_intermediate_count: number
  total_problems_intermediate_count: number
  solved_problems_advanced_count: number
  total_problems_advanced_count: number
  top_users: ProblemStatsTopUser[]
};

// get user's problem stats
export const getProblemStats = async (accessToken: string | null): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.get(
    PROBLEM_STATS_API_URL,
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        ...(accessToken && {
          Authorization: `JWT ${accessToken}`
        })
      }
    }
  );

  return response;
};

// like problem
export const likeProblem = async (accessToken: string | null, problemIDOrSlug: number | string): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.post(
    generatePath(PROBLEM_LIKE_API_URL, { key: problemIDOrSlug.toString() }),
    {},
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        ...(accessToken && {
          Authorization: `JWT ${accessToken}`
        })
      }
    }
  );

  return response;
};

// dislike problem
export const dislikeProblem = async (accessToken: string | null, problemIDOrSlug: number | string): Promise<AxiosResponse> => {
  const response: AxiosResponse = await axios.post(
    generatePath(PROBLEM_DISLIKE_API_URL, { key: problemIDOrSlug.toString() }),
    {},
    {
      baseURL: process.env.REACT_APP_SIGMOID_API_BASE_URL,
      headers: {
        ...(accessToken && {
          Authorization: `JWT ${accessToken}`
        })
      }
    }
  );

  return response;
};
